import { z } from 'zod';
import { Email, IsoDateTime, TimeZone, Uuid, patchOf, withDefaults } from './common.js';
import { EvacuationKind, RollCallStatus } from './evacuation.js';
import { DevicePlatform, HealthReport, PrinterModel, RemoteActionKind } from './kiosk.js';
import { BadgeMedia } from './badges.js';
import { VisitorLocale } from './languages.js';
/**
 * Module `shared-reception`: one kiosk shared by several companies (tenants) in one building (ADR 0034,
 * docs/modules/shared-reception.md). Written by the architect in task SR0; builders never change this file.
 *
 * Words: building = one shared reception (`sr_buildings`); membership = one company in one building with one of its sites;
 * building device = the shared tablet (`sr_devices`); link = that tablet's ordinary `devices` row inside one member tenant,
 * with its own device token; marshal phone = a building marshal's Companion app enrolled for the building (`sr_marshal_phones`).
 */
// ---------- Constants ----------
export const SR_MODULE_KEY = 'shared-reception';
/** Header every request from a company slot on a shared kiosk carries: the slot's tenant id. The server refuses a mismatch with the token's tenant (409 `error.sr_wrong_tenant`). */
export const SR_TENANT_HEADER = 'x-cloudsign-tenant';
/** Authorization schemes: the shared tablet's building token and a marshal phone's token. */
export const SR_AUTH_SCHEME = { device: 'SharedKiosk', phone: 'SrPhone' };
/**
 * Marshal phone invitation tokens start with this, so the Companion app's existing `/c/<token>` link and QR handling (no native
 * change) can tell a building enrolment from a member invite.
 */
export const SR_PHONE_INVITE_PREFIX = 'srm_';
/** Remote actions a company may send to its own link on a shared kiosk. `nfc_enrol` only when the building allows it. */
export const SR_LINK_ACTIONS = ['reload_config'];
/** Jobs of the module (contract s8). The definer `app_sr_enqueue_for_members` accepts only the first three. */
export const SR_JOBS = {
    startMember: 'shared-reception.evacuation.start-member',
    endMember: 'shared-reception.evacuation.end-member',
    applyMark: 'shared-reception.evacuation.apply-mark',
    drain: 'shared-reception.links.drain',
    offlineScan: 'shared-reception.devices.offline-scan',
    retention: 'shared-reception.rollcall.retention',
    onSiteReconcile: 'shared-reception.onsite.reconcile',
    purge: 'shared-reception.purge',
    /** SECSR1: ends evacuations nobody ended and warns when one has run for a long time. */
    evacuationWatch: 'shared-reception.evacuation.watch',
    /** SECSR1: emails a company's approvers when a new shared tablet asks for a link. */
    notifyNewTablets: 'shared-reception.links.notify-new',
};
/** Domain events published by the module (tenant outbox unless marked). Not webhook events in this version. */
export const SR_EVENTS = {
    invited: 'shared-reception.membership.invited',
    accepted: 'shared-reception.membership.accepted',
    paused: 'shared-reception.membership.paused',
    resumed: 'shared-reception.membership.resumed',
    left: 'shared-reception.membership.left',
    declined: 'shared-reception.membership.declined',
    /** Platform level (tenant_id NULL). Header and counts only. */
    evacuationStarted: 'shared-reception.building.evacuation_started',
    evacuationEnded: 'shared-reception.building.evacuation_ended',
};
/** Message keys of the module's refusals (all under `error.`; i18n in phase5/sr-arch.ts). */
export const SR_ERROR_KEYS = [
    'error.sr_wrong_tenant',
    'error.sr_building_full',
    'error.sr_already_member',
    'error.sr_timezone_mismatch',
    'error.sr_remote_site',
    'error.sr_site_in_building',
    'error.sr_consent_outdated',
    'error.sr_link_token_already_issued',
    'error.sr_link_action_not_allowed',
    'error.sr_not_building_staff',
    'error.sr_role_required',
    'error.sr_platform_off',
    'error.sr_phone_list_off',
    'error.sr_phone_start_off',
    'error.sr_invite_invalid',
    'error.sr_bad_pin',
    'error.sr_kiosk_start_off',
    'error.sr_drill_needs_pin',
    'error.sr_leave_to_remove_link',
];
// ---------- Enums ----------
export const BuildingRole = z.enum(['building_admin', 'building_marshal']);
export const SrMembershipStatus = z.enum(['invited', 'active', 'paused', 'leaving', 'left', 'declined', 'withdrawn']);
/** Memberships that have a link on the shared kiosk. */
export const SR_LINKED_STATUSES = ['active', 'paused', 'leaving'];
export const SrBuildingStatus = z.enum(['active', 'archived']);
export const SrKioskStart = z.enum(['off', 'pin', 'open']);
export const SrEvacuationSource = z.enum(['kiosk', 'building', 'member', 'phone', 'system']);
export const SrMarkSource = z.enum(['building_marshal', 'building_kiosk', 'marshal_phone']);
// ---------- Settings ----------
/** Platform settings, module `shared-reception` (contract s3). `enabled` is the master switch, off by default. */
export const SharedReceptionPlatformSettings = z.object({
    enabled: z.boolean().default(false),
    maxCompaniesPerBuilding: z.number().int().min(1).max(100).default(20),
    pairingCodeMinutes: z.number().int().min(1).max(1440).default(15),
    /** How long a leaving company's link may still send queued events. 0 = revoke at once. */
    leaveDrainHours: z.number().int().min(0).max(720).default(72),
    /** Names in the building roll call are deleted this long after the building evacuation ends. 0 = when it ends. */
    rollCallRetentionDays: z.number().int().min(0).max(365).default(7),
    /** The same for names from a practice drill (SECSR1: a drill list is deleted sooner). 0 = when it ends. */
    drillRetentionDays: z.number().int().min(0).max(365).default(1),
    /** Building evacuation headers and counts are kept this long. */
    evacuationRetentionDays: z.number().int().min(1).max(3650).default(1095),
    /** Current sharing statement version (`sr.consent.v<n>.*`). Raising it asks companies to accept again before they resume. */
    consentVersion: z.number().int().min(1).max(99).default(1),
    /** Marshal phone invitations expire after this many hours. */
    phoneInviteHours: z.number().int().min(1).max(168).default(24),
    /** A marshal phone must be enrolled again after this many days. 0 = no expiry (revocation still works). */
    phoneTokenDays: z.number().int().min(0).max(730).default(180),
    /** A building-area session ends after this many idle minutes. 0 = the platform's general idle limit applies. */
    buildingSessionIdleMinutes: z.number().int().min(0).max(1440).default(30),
    /** A shared tablet may start (sync) at most this many building evacuations per hour without the PIN (real ones only). 0 = no limit. */
    kioskSyncStartsPerHour: z.number().int().min(0).max(100).default(3),
    /** Nightly purge: finished remote commands, used or expired phone invitations, cleared tablet alerts and used or expired pairing codes are deleted after this many days. 0 = keep. */
    commandRetentionDays: z.number().int().min(0).max(3650).default(30),
    phoneInviteRetentionDays: z.number().int().min(0).max(3650).default(30),
    deviceAlertRetentionDays: z.number().int().min(0).max(3650).default(180),
    pairingCodeRetentionDays: z.number().int().min(0).max(3650).default(7),
    /** SECSR1: a building may have at most this many paired tablets (checked when a pairing code is made and when it is used). 0 = no limit. */
    maxTabletsPerBuilding: z.number().int().min(0).max(100).default(4),
    /** SECSR1: a link that has never been used may be given a token again only within this many minutes of being created (or after an admin reset). */
    linkReissueMinutes: z.number().int().min(1).max(1440).default(15),
    /** SECSR1: a building evacuation may not be started or synced with a start time older than this many hours (it is moved to the limit). 0 = no limit. */
    maxEvacuationBackdateHours: z.number().int().min(0).max(720).default(24),
    /** SECSR1: revoked or expired marshal phone rows are deleted after this many days. 0 = keep. */
    marshalPhoneRetentionDays: z.number().int().min(0).max(3650).default(30),
    /** SECSR1: building totals are shown to a company only when the building has at least this many companies (with fewer, a company could work out the other's numbers). 0 or 1 = always. */
    minCompaniesForTotals: z.number().int().min(0).max(100).default(3),
});
const LocalisedText = z.record(z.string(), z.string().max(200));
/** Building settings (`sr_buildings.settings`), edited by the building admin or the platform admin. */
export const SharedReceptionBuildingSettings = z.object({
    /** Welcome text per locale; empty shows `sr.kiosk.welcome_default`. */
    welcomeText: LocalisedText.default({}),
    /** Short privacy line on the welcome screen; empty shows `sr.kiosk.privacy_default`. */
    privacyText: LocalisedText.default({}),
    /** Offered on the welcome screen. A company's flow uses the chosen language if that company offers it, else its default. */
    languages: z.array(VisitorLocale).min(1).max(10).default(['en-GB']),
    /** Show a search box on the company picker from this many companies. 0 = never. */
    companyPickerSearchFrom: z.number().int().min(0).max(100).default(12),
    /** Back to the building welcome after this many seconds without a touch. 0 = never (not recommended). */
    idleResetSeconds: z.number().int().min(0).max(600).default(60),
    healthReportSeconds: z.number().int().min(15).max(3600).default(60),
    alerts: withDefaults(z.object({
        /** Email the building's alert address when the tablet has not reported for this long. 0 = off. */
        offlineMinutes: z.number().int().min(0).max(1440).default(10),
        batteryPercent: z.number().int().min(0).max(100).default(15),
        printerFault: z.boolean().default(true),
        appOutdated: z.boolean().default(true),
    })),
    evacuation: withDefaults(z.object({
        /** Starting a building evacuation from the shared kiosk. */
        kioskStart: SrKioskStart.default('pin'),
        /**
         * A member company's REAL evacuation at its member site starts the building's (Perry, 26 Oct 2026: a real evacuation
         * started by any member company evacuates the whole building; default on).
         */
        memberStartsBuilding: z.boolean().default(true),
        /** Same for drills. Off: drills stay with the company (Perry, 26 Oct 2026). */
        memberDrillsBuilding: z.boolean().default(false),
        /** Show Visitor / Staff / Contractor on building lists (each company can also turn it off for itself). */
        showGroupLabel: z.boolean().default(true),
        /** SECSR1: an evacuation nobody ended is ended automatically after this many hours (0 = never). */
        autoEndHours: withDefaults(z.object({
            drill: z.number().int().min(0).max(720).default(12),
            real: z.number().int().min(0).max(720).default(24),
        })),
        /** SECSR1: email the building's alert address when an evacuation has been active longer than this many hours. 0 = off. */
        longRunningAlertHours: z.number().int().min(0).max(720).default(4),
    })),
    /** Building fire marshals' phone app (Perry, 26 Oct 2026: marshals get the Companion app with an offline building list). */
    marshalPhones: withDefaults(z.object({
        /** Off: no phone invitations, enrolled phones are refused, no on-site list is kept for phones. */
        enabled: z.boolean().default(true),
        /** The phone refreshes its cached on-site list this often while open. */
        refreshMinutes: z.number().int().min(1).max(60).default(5),
        /** The phone deletes its cached list when it is older than this (it then shows "no current list"). Not 0. */
        cacheExpiryHours: z.number().int().min(1).max(48).default(12),
        /** Marshals may start a building evacuation from the phone (also offline). */
        canStart: z.boolean().default(true),
        /** The phone app lock (PIN or fingerprint) must be on before the list is shown. */
        requireAppLock: z.boolean().default(true),
    })),
    /** Companies may put the shared kiosk into NFC enrolment mode for their own cards. */
    allowCompanyNfcEnrolment: z.boolean().default(false),
});
/** Tenant settings, module `shared-reception`. */
export const SharedReceptionTenantSettings = z.object({
    rollCall: withDefaults(z.object({
        /** Include this company's group label (Visitor / Staff / Contractor) in the building lists. */
        shareGroupLabel: z.boolean().default(true),
        /**
         * Keep a minimal "on site now" list (name, company, group label, signed-in time) for the building's marshal phones, so
         * they have a list offline. Off: this company's people appear on phones only during an evacuation (and on the kiosk).
         */
        shareOnSiteForPhones: z.boolean().default(true),
    })),
    /** Which device fault emails this company's site gets for its shared links. */
    linkAlerts: z.enum(['none', 'queue_only', 'all']).default('queue_only'),
    /** Show building totals on this company's evacuation screen. */
    buildingTotalsOnEvacuation: z.boolean().default(true),
    /**
     * SECSR1: a new tablet asking for a link to this company must be approved here first (no token is handed out until then).
     * Off: new tablets of buildings this company belongs to are linked without asking (not recommended).
     */
    approveNewSharedTablets: z.boolean().default(true),
});
// ---------- Buildings (platform) ----------
const Name = z.string().trim().min(1).max(120);
export const SrBuilding = z.object({
    id: Uuid,
    name: z.string(),
    address: z.string(),
    timeZone: TimeZone,
    operatorName: z.string(),
    operatorContactEmail: z.string(),
    operatorTenantId: Uuid.nullable(),
    status: SrBuildingStatus,
    /** Building fault emails; '' = none. */
    alertEmail: z.string(),
    settings: SharedReceptionBuildingSettings,
    /** Memberships active, paused or leaving. */
    companies: z.number().int(),
    devices: z.number().int(),
    /** True once a building PIN is set (the PIN itself is never returned). */
    pinSet: z.boolean(),
    createdAt: IsoDateTime,
});
export const SrBuildingCreate = z.object({
    name: Name,
    address: z.string().trim().min(1).max(300),
    timeZone: TimeZone,
    operatorName: Name,
    operatorContactEmail: Email,
    operatorTenantId: Uuid.nullable().optional(),
    alertEmail: z.union([z.literal(''), Email]).optional(),
});
/** PATCH /platform/shared-receptions/:id. `settings` is a partial patch merged into the stored settings. */
export const SrBuildingUpdate = patchOf(SrBuildingCreate).extend({ settings: z.record(z.string(), z.unknown()).optional() });
/** PATCH /building/settings (building admin). */
export const SrBuildingSettingsPatch = z.object({
    settings: z.record(z.string(), z.unknown()).optional(),
    alertEmail: z.union([z.literal(''), Email]).optional(),
});
export const SrInvite = z.object({ tenantId: Uuid });
export const SrOrder = z.object({ membershipIds: z.array(Uuid).min(1).max(100) });
export const SrPinSet = z.object({ pin: z.string().regex(/^\d{4,8}$/) });
// ---------- Memberships ----------
export const SrMembership = z.object({
    id: Uuid,
    buildingId: Uuid,
    buildingName: z.string(),
    buildingAddress: z.string(),
    buildingTimeZone: TimeZone,
    operatorName: z.string(),
    tenantId: Uuid,
    /** Platform and building views only; never sent to another tenant. */
    tenantName: z.string().optional(),
    siteId: Uuid.nullable(),
    siteName: z.string().nullable(),
    status: SrMembershipStatus,
    displayName: z.string().nullable(),
    sort: z.number().int(),
    consentVersion: z.number().int().nullable(),
    currentConsentVersion: z.number().int(),
    /** An active membership whose accepted version is older than the current one: it is off the kiosk until the company accepts again. */
    needsReaccept: z.boolean().optional(),
    invitedAt: IsoDateTime,
    acceptedAt: IsoDateTime.nullable(),
    leavingUntil: IsoDateTime.nullable(),
});
/** POST /shared-reception/memberships/:id/accept. `consentVersion` must equal the platform's current version. */
export const SrAccept = z.object({
    siteId: Uuid,
    displayName: z.string().trim().min(1).max(60),
    consentVersion: z.number().int().min(1),
});
export const SrMembershipUpdate = z.object({ displayName: z.string().trim().min(1).max(60) });
/** POST /memberships/:id/resume: the consent version the user saw (must be current). */
export const SrResume = z.object({ consentVersion: z.number().int().min(1) });
/**
 * GET /shared-reception/consent/:version: the sharing statement the company accepts, the data-sharing agreement it links to
 * (task D-SR-LEGAL; `draft: true` until Perry and a lawyer have reviewed it) and suggested lines for the company's visitor
 * privacy notice. All text comes from i18n (`sr.consent.v<n>.*`, `sr.dsa.v<n>.*`, `sr.privacy_notice.v<n>.*`).
 */
export const SrConsent = z.object({
    version: z.number().int(),
    title: z.string(),
    /** Paragraphs, in order. */
    body: z.array(z.string()),
    /** What is shared, as a plain list for the "what we share" box. */
    shared: z.array(z.string()),
    agreement: z.object({
        title: z.string(),
        draft: z.boolean(),
        sections: z.array(z.object({ heading: z.string(), body: z.string() })),
    }),
    privacyNoticeLines: z.array(z.string()),
});
/** Number of numbered sections in each data-sharing agreement version (keys `sr.dsa.v<n>.s<i>.heading|body`). */
export const SR_AGREEMENT_SECTIONS = { 1: 10 };
/** Number of consent paragraphs, "what we share" items and privacy-notice lines per version. */
export const SR_CONSENT_PARTS = {
    1: { paragraphs: 6, shared: 6, privacyLines: 3 },
};
// ---------- Building staff and the building area ----------
export const SrStaff = z.object({ accountId: Uuid, email: z.string(), fullName: z.string(), role: BuildingRole, addedAt: IsoDateTime, phones: z.number().int() });
export const SrStaffAdd = z.object({ email: Email, fullName: z.string().trim().min(1).max(120).optional(), role: BuildingRole });
/** Area picker entry (session state and capabilities). */
export const SrBuildingChoice = z.object({ buildingId: Uuid, name: z.string(), role: BuildingRole });
/** POST /auth/select-building (C-SR1): enter the building area. 2FA always required. */
export const SelectBuildingRequest = z.object({ buildingId: Uuid });
export const SrBuildingMe = z.object({ building: SrBuilding, role: BuildingRole });
/** GET /building/companies: no tenant ids, no site names. */
export const SrBuildingCompany = z.object({
    membershipId: Uuid,
    displayName: z.string().nullable(),
    status: SrMembershipStatus,
    available: z.boolean(),
    sort: z.number().int(),
    /** False when the company turned off the on-site list for marshal phones. */
    sharesOnSiteList: z.boolean(),
});
// ---------- Building devices ----------
export const SrPrinterConfig = z.object({
    model: PrinterModel,
    host: z.string().trim().min(1).max(253).regex(/^[A-Za-z0-9.-]+$/),
    port: z.number().int().min(1).max(65535).default(9100),
    media: BadgeMedia,
});
/** SECSR1: whether the company has approved this tablet (`pending`: no token yet; `denied`: refused). */
export const SrLinkApproval = z.enum(['pending', 'approved', 'denied']);
/** A shared tablet waiting for a company's approval (GET /shared-reception/memberships/:id/pending-links). Building and tablet names only. */
export const SrPendingLink = z.object({
    linkDeviceId: Uuid,
    buildingId: Uuid,
    buildingName: z.string(),
    deviceName: z.string(),
    requestedAt: IsoDateTime,
});
export const SrDevice = z.object({
    id: Uuid,
    buildingId: Uuid,
    name: z.string(),
    status: z.enum(['active', 'revoked']),
    online: z.boolean(),
    pairedAt: IsoDateTime,
    lastSeenAt: IsoDateTime.nullable(),
    platform: DevicePlatform.nullable(),
    model: z.string().nullable(),
    appVersion: z.string().nullable(),
    bundleVersion: z.string().nullable(),
    printer: SrPrinterConfig.nullable(),
    health: HealthReport.omit({ deviceTime: true, queueLength: true, deadLetters: true }).extend({ reportedAt: IsoDateTime }).nullable(),
    alertsEnabled: z.boolean(),
    /** Building view: company display names only (platform view adds tenantId). */
    links: z.array(z.object({ tenantId: Uuid.optional(), displayName: z.string().nullable(), status: SrMembershipStatus, approval: SrLinkApproval.optional() })),
});
export const SrDeviceUpdate = z.object({
    name: z.string().trim().min(1).max(60).optional(),
    printer: SrPrinterConfig.nullable().optional(),
    alertsEnabled: z.boolean().optional(),
});
export const SrPairingCodeCreate = z.object({ name: z.string().trim().min(1).max(60) });
/** Shown once. Same 9-digit format and the same global pairing guard as tenant codes (ADR 0008). */
export const SrPairingCode = z.object({ code: z.string().regex(/^\d{9}$/), expiresAt: IsoDateTime });
/** POST /building/devices/:id/actions. Building admins may send every kind a kiosk understands except `nfc_enrol` and `evacuation_mode`. */
export const SrDeviceAction = z.discriminatedUnion('action', [
    z.object({ action: z.literal('reload_config') }),
    z.object({ action: z.literal('restart_app') }),
    z.object({ action: z.literal('test_print') }),
    z.object({ action: z.literal('show_message'), message: z.string().trim().min(1).max(200), seconds: z.number().int().min(5).max(3600).default(60) }),
    z.object({ action: z.literal('unpair') }),
]);
// ---------- The shared kiosk (building token) ----------
export const SrEvacuationHeader = z.object({
    id: Uuid,
    kind: EvacuationKind,
    status: z.enum(['active', 'ended']),
    startedAt: IsoDateTime,
    startedSource: SrEvacuationSource,
    startedLabel: z.string(),
    endedAt: IsoDateTime.nullable(),
});
export const SrKioskCompany = z.object({
    tenantId: Uuid,
    linkDeviceId: Uuid,
    displayName: z.string(),
    sort: z.number().int(),
    state: z.enum(SR_LINKED_STATUSES),
    /** False when paused, leaving, suspended, read-only after grace, or the company's module is off. Never the reason. */
    available: z.boolean(),
});
/** GET /shared-reception/device/config (building token, ETag). */
export const SharedKioskConfig = z.object({
    building: z.object({
        id: Uuid,
        name: z.string(),
        timeZone: TimeZone,
        welcomeText: LocalisedText,
        privacyText: LocalisedText,
        languages: z.array(VisitorLocale),
        defaultLanguage: VisitorLocale,
    }),
    device: z.object({ id: Uuid, name: z.string(), printer: SrPrinterConfig.nullable() }),
    companies: z.array(SrKioskCompany),
    evacuation: z.object({
        kioskStart: SrKioskStart,
        /** kdf1 hash of the building PIN with the building key, checked on the tablet when offline; null = no PIN set. */
        pinHash: z.string().nullable(),
        /**
         * base64url key the tablet needs to check `pinHash` offline (the h2$ form is wrapped with it, as site PINs are with `site.pinKey`).
         * Derived per building, so it opens nothing at another building or at any company's site. Null whenever `pinHash` is null.
         * Optional so older tablet bundles still parse the config (added in S-SR1).
         */
        pinKey: z.string().nullable().optional(),
        memberStartsBuilding: z.boolean(),
        showGroupLabel: z.boolean(),
        active: SrEvacuationHeader.nullable(),
    }),
    settings: z.object({ companyPickerSearchFrom: z.number().int(), idleResetSeconds: z.number().int(), healthReportSeconds: z.number().int() }),
    generatedAt: IsoDateTime,
});
/** POST /shared-reception/device/links: a token appears once per link, ever (until an admin resets the link). */
export const SrLinkTokens = z.object({
    links: z.array(z.object({
        tenantId: Uuid,
        linkDeviceId: Uuid,
        /** null with `errorKey` when the link already had a token (`error.sr_link_token_already_issued`). */
        token: z.string().nullable(),
        errorKey: z.string().nullable(),
    })),
});
/** `KioskConfig.sections['shared-reception']` of a company's link: tells the kiosk it is a link. */
export const KioskSharedReceptionSection = z.object({
    buildingId: Uuid,
    displayName: z.string(),
    remoteActions: z.array(RemoteActionKind),
    shareGroupLabel: z.boolean(),
});
/** POST /shared-reception/device/health: tablet hardware plus each link's queue. */
export const SrHealthReport = HealthReport.omit({ queueLength: true, deadLetters: true, oldestQueuedAt: true, lastSignInAt: true }).extend({
    links: z.array(z.object({ tenantId: Uuid, queued: z.number().int().min(0), deadLetters: z.number().int().min(0), oldestQueuedAt: IsoDateTime.nullable() })).max(100),
    /** Events copied off a wiped company slot that could not be sent (never deleted by the app). */
    orphanedEvents: z.number().int().min(0),
});
export const SrHealthResponse = z.object({ serverTime: IsoDateTime, configEtag: z.string(), nextReportSeconds: z.number().int() });
/**
 * A company link's own health report to the existing `/kiosk-devices/device/health` every 5 minutes: only its queue and
 * versions. Battery, network and printer belong to the building and are left out (C-SR2 accepts this shape for links).
 */
export const LinkHealthReport = HealthReport.partial({ batteryPercent: true, charging: true, wifiStrength: true, freeStorageMb: true, printer: true, network: true }).extend({
    shared: z.literal(true),
});
// ---------- Building evacuation ----------
export const SrTotals = z.object({ unaccounted: z.number().int(), safe: z.number().int(), missing: z.number().int(), offSite: z.number().int() });
/** One person on the building roll call: the minimal fields only (ADR 0034 s5). */
export const SrRollCallRow = z.object({
    id: Uuid,
    /** Only on the shared tablet's own routes; marshals, phones and the building area see a company by its label. */
    tenantId: Uuid.optional(),
    companyLabel: z.string(),
    displayName: z.string(),
    groupLabel: z.string().nullable(),
    signedInAt: IsoDateTime.nullable(),
    status: RollCallStatus,
    statusAt: IsoDateTime.nullable(),
    statusSource: z.string().nullable(),
    musterPointName: z.string().nullable(),
    check: z.boolean(),
    /** The on-site row this person came from, so marks made offline on a phone can be matched. */
    onSiteId: Uuid.nullable(),
});
export const SrEvacuationView = z.object({
    header: SrEvacuationHeader,
    rows: z.array(SrRollCallRow),
    totals: SrTotals,
    byCompany: z.array(z.object({ tenantId: Uuid.optional(), companyLabel: z.string(), totals: SrTotals })),
});
/** Building area and phone: start. The kiosk adds the PIN (SrDeviceEvacuationStart). */
export const SrEvacuationStart = z.object({ kind: EvacuationKind.default('real'), id: Uuid.optional(), startedAt: IsoDateTime.optional() });
export const SrDeviceEvacuationStart = SrEvacuationStart.extend({ pin: z.string().max(20).nullable().default(null) });
export const SrDeviceEvacuationEnd = z.object({ pin: z.string().max(20).nullable().default(null) });
export const SrEvacuationStartResult = z.object({ header: SrEvacuationHeader, alreadyActive: z.boolean() });
/** A mark by a building marshal (portal or phone). Exactly one of rollCallId / onSiteId (phone marks made offline use onSiteId). */
export const SrBuildingMark = z
    .object({
    rollCallId: Uuid.optional(),
    onSiteId: Uuid.optional(),
    status: RollCallStatus,
    musterPointName: z.string().trim().max(80).optional(),
    idempotencyKey: Uuid,
    occurredAt: IsoDateTime,
})
    .refine((m) => (m.rollCallId === undefined) !== (m.onSiteId === undefined), { message: 'rollCallId_or_onSiteId' });
export const SrMarkResult = z.object({ applied: z.boolean(), duplicate: z.boolean(), row: SrRollCallRow.nullable() });
/** POST /shared-reception/device/evacuations/sync (building token): maps the building evacuation to each company's evacuation. */
export const SrDeviceEvacuationSync = z.object({
    header: z.object({ id: Uuid, kind: EvacuationKind, startedAt: IsoDateTime }),
    members: z.array(z.object({ tenantId: Uuid, tenantEvacuationId: Uuid })).max(100),
    /** The building PIN. A REAL evacuation syncs without it (fire safety); a drill needs it when the building asks for a PIN. */
    pin: z.string().max(20).nullable().optional(),
});
export const SrEvacuationSyncResult = z.object({ id: Uuid, mergedInto: Uuid.nullable() });
/** History lists: headers and counts only, never names. */
export const SrEvacuationSummary = SrEvacuationHeader.extend({ totals: SrTotals, companies: z.number().int(), namesDeleted: z.boolean() });
/** GET /shared-reception/evacuations/active?siteId (a company's users with evacuation.view): combined counts only. */
export const SrBuildingTotals = z.object({ buildingName: z.string(), header: SrEvacuationHeader, totals: SrTotals });
// ---------- Marshal phones (Companion app for building marshals) ----------
/** POST /building/phones/invitations (building admin): emails the staff member a one-off enrolment link. */
export const SrPhoneInviteCreate = z.object({ accountId: Uuid });
export const SrPhoneInvite = z.object({ emailMasked: z.string(), expiresAt: IsoDateTime });
export const SrMarshalPhone = z.object({
    id: Uuid,
    accountId: Uuid,
    fullName: z.string(),
    role: BuildingRole,
    platform: DevicePlatform.nullable(),
    model: z.string().nullable(),
    appVersion: z.string().nullable(),
    enrolledAt: IsoDateTime,
    lastSeenAt: IsoDateTime.nullable(),
    expiresAt: IsoDateTime.nullable(),
    revokedAt: IsoDateTime.nullable(),
});
/** POST /shared-reception/phone/enrol (public, rate-limited). `token` is the `srm_...` invitation token. */
export const SrPhoneEnrol = z.object({
    token: z.string().min(24).max(200).startsWith(SR_PHONE_INVITE_PREFIX),
    device: z.object({
        platform: DevicePlatform,
        osVersion: z.string().max(40),
        appVersion: z.string().max(40),
        bundleVersion: z.string().max(40),
        model: z.string().max(80),
    }),
});
export const SrPhoneSettings = z.object({
    refreshMinutes: z.number().int(),
    cacheExpiryHours: z.number().int(),
    canStart: z.boolean(),
    requireAppLock: z.boolean(),
    showGroupLabel: z.boolean(),
});
/** GET /shared-reception/phone/config. */
export const SrPhoneConfig = z.object({
    building: z.object({ id: Uuid, name: z.string(), timeZone: TimeZone }),
    phoneId: Uuid,
    fullName: z.string(),
    role: BuildingRole,
    settings: SrPhoneSettings,
    activeEvacuation: SrEvacuationHeader.nullable(),
    generatedAt: IsoDateTime,
});
/** The enrolment answer: the phone token appears once (store it in secure storage under `sr:phone`). */
export const SrPhoneEnrolResult = SrPhoneConfig.extend({ phoneToken: z.string() });
/** One person on site now at a member company, for the phone's offline list. Minimal fields only. */
export const SrOnSiteRow = z.object({
    id: Uuid,
    companyLabel: z.string(),
    displayName: z.string(),
    groupLabel: z.string().nullable(),
    signedInAt: IsoDateTime,
});
/** GET /shared-reception/phone/on-site. The phone keeps it until `expiresAt`, then deletes it. */
export const SrOnSiteList = z.object({
    generatedAt: IsoDateTime,
    expiresAt: IsoDateTime,
    rows: z.array(SrOnSiteRow),
    /** Companies whose people are not on this list (turned the phone list off, or unavailable): names only, so the marshal knows to check them. */
    notShared: z.array(z.string()),
});
/** POST /shared-reception/phone/evacuations/sync: an evacuation started or marked offline on the phone. */
export const SrPhoneEvacuationSync = z.object({
    header: z.object({ id: Uuid, kind: EvacuationKind, startedAt: IsoDateTime }),
    marks: z.array(SrBuildingMark).max(500),
});
export const SrPhoneEvacuationSyncResult = z.object({ id: Uuid, mergedInto: Uuid.nullable(), applied: z.number().int(), duplicates: z.number().int(), unmatched: z.number().int() });
// ---------- Realtime (channel `building`, rooms b:<buildingId>:building) ----------
export const SrLiveMessage = z.discriminatedUnion('type', [
    z.object({ type: z.literal('config.changed') }),
    z.object({ type: z.literal('members.changed') }),
    z.object({ type: z.literal('command'), commandId: Uuid }),
    z.object({ type: z.literal('sr.evac.state'), header: SrEvacuationHeader }),
    z.object({ type: z.literal('sr.evac.row'), row: SrRollCallRow }),
    z.object({ type: z.literal('refresh') }),
]);
//# sourceMappingURL=shared-reception.js.map